import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

import { useAuth } from '../../services/auth/AuthProvider';
import { images } from '../../utils/constants/constant';

import './login.css';
import { ArrowRightIcon } from 'lucide-react';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [username, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { state, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (state.user) {
      let fromForAdmin =
        location.state?.from?.pathname + location.state?.from?.search ||
        '/admin/home';
      let fromForSupervisor =
        location.state?.from?.pathname + location.state?.from?.search ||
        '/supervisor/home';

      if (state.user?.userType === 2) {
        fromForAdmin.includes('/supervisor/home') && (fromForAdmin = '/admin/home');
        navigate(fromForAdmin, { replace: true });
      } else if (state.user?.userType === 3) {
        fromForSupervisor.includes('/admin/home') &&
          (fromForSupervisor = '/supervisor/home');
        navigate(fromForSupervisor, { replace: true });
      } else {
        navigate(fromForAdmin, { replace: true });
      }
    }
  }, [state.user, location.state, navigate]);

  const validateForm = () => username.length > 0 && password.length > 0;

  const handleLogin = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (!validateForm()) return;

    axios
      .post('/api/userRoutes/login', {
        username,
        password,
      })
      .then(() => {
        if (rememberMe && username) {
          try {
            sessionStorage.setItem('loginRememberUser', username);
          } catch {
            /* ignore */
          }
        } else {
          try {
            sessionStorage.removeItem('loginRememberUser');
          } catch {
            /* ignore */
          }
        }
        login();
      })
      .catch(() => setError('Invalid credentials. Please try again.'));
  };

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('loginRememberUser');
      if (saved) setEmail(saved);
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <div className="login-container">
      <div className="login-wrapper">
        <div className="login-left-panel">
          <div className="login-logo-section">
            <div className="login-logo-wrapper">
              <img
                className="login-logo"
                src={images.logoWhite}
                alt="Mushin"
              />
            </div>
            <div className="login-hero-content">
              <h1 className="login-hero-title">
                The <br />{' '}
                <span className="highlight">
                  Operating System
                  <br />
                  For Factory
                </span>{' '}
                <br />
                Operations
              </h1>
              <p className="login-hero-text">
                One connected system for quality, production, traceability, audits,
                maintenance, and more. Built to digitize every critical function on the
                plant floor.
              </p>
            </div>
          </div>
        </div>

        <div className="login-right-panel">
          <div className="login-form-container">
            <div className="login-header">
              <div>
                <h1 className="login-title">
                  Welcome back
                </h1>
                <p className="login-subtitle">
                  Sign in to your portal
                </p>
              </div>
            </div>
            <img src={images.companyImg} alt="Company Logo" className="customer-logo" />

            <form className="login-form" onSubmit={handleLogin}>
              <div className="form-group">
                <label
                  className="form-label"
                  htmlFor="login-email"
                >
                  User id / email
                </label>
                <input
                  className="form-input"
                  id="login-email"
                  name="email"
                  type="text"
                  autoComplete="username"
                  placeholder="you@company.com"
                  value={username}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="form-group password-group">
                <label
                  className="form-label"
                  htmlFor="login-password"
                >
                  Password
                </label>
                <div className="password-input-wrapper">
                  <input
                    className="form-input password-input"
                    id="login-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    className="password-toggle-btn"
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <p
                  className="error-message"
                  role="alert"
                >
                  {error}
                </p>
              )}

              <button
                className="login-button"
                type="submit"
                disabled={!validateForm()}
              >
                Sign in
                <ArrowRightIcon size={16} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
